import { useState } from 'react';
import { Control, Controller, FieldValues, Path, RegisterOptions } from 'react-hook-form';

interface InputFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  label: string;
  type?: string;
  rules?: RegisterOptions<T>;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  endIconClassName?: string;
}

const InputField = <T extends FieldValues>({
  control,
  name,
  label,
  type = 'text',
  rules,
  startIcon,
  endIcon,
  endIconClassName,
}: InputFieldProps<T>) => {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field, fieldState: { error } }) => {
        const isActive = isFocused || !!field.value;

        return (
          <div className="relative">
            <input
              {...field}
              onFocus={() => setIsFocused(true)}
              onBlur={(e) => {
                field.onBlur();
                setIsFocused(false);
              }}
              type={type}
              placeholder=" "
              className={`peer relative w-full h-[3.125rem] rounded-lg bg-opacity-0 px-2 z-10 focus:border-[#B89A1D] focus:border-[0.113rem] focus:border-t-[0.056rem] disabled:bg-gray-200 disabled:cursor-not-allowed disabled:border-0 outline-none transition-all duration-75 border ${
                error ? 'border-red-500' : 'border-gray-400'
              } ${startIcon ? 'pl-10' : ''} ${endIcon ? 'pr-10' : ''}`}
            />

            <label
              className={`
                absolute z-10 bg-white px-1 leading-tight text-base transition-all duration-300 pointer-events-none
                ${startIcon ? 'left-10' : 'left-4'}
                top-3 text-gray-500
                peer-focus:-top-2 peer-focus:text-[0.906rem] peer-focus:text-[#B89A1D] peer-[&:not(:placeholder-shown)]:-top-2
            `}
            >
              {label}
            </label>

            {startIcon && (
              <div
                className={`tranition duration-300 absolute left-3 top-1/2 z-20 -translate-y-1/2 ${isActive ? '!text-[#B89A1D]' : 'text-slate-400'}`}
              >
                {startIcon}
              </div>
            )}

            {endIcon && (
              <div className={`absolute right-3 top-1/2 z-20 -translate-y-1/2 ${endIconClassName}`}>
                {endIcon}
              </div>
            )}

            {/* {error && <p className="mt-1 text-xs text-red-500">{error.message}</p>} */}
          </div>
        );
      }}
    />
  );
};

export default InputField;
